import React, { useState, useEffect } from 'react';
import { userManagementAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';

const UserManagement = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const { user: currentUser } = useAuth();

  useEffect(() => {
    fetchUsers();
    fetchStats();
  }, [search, roleFilter]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = {};
      if (search) params.search = search;
      if (roleFilter) params.role = roleFilter;

      const response = await userManagementAPI.getAll(params);
      setUsers(response.data.users || []);
      setError('');
    } catch (err) {
      setError(t('userManagement.failedToLoad'));
      console.error('Users error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await userManagementAPI.getStats();
      setStats(response.data);
    } catch (err) {
      console.error('Stats error:', err);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    if (!window.confirm(t('userManagement.roleChangeConfirm', { role: newRole }))) {
      return;
    }

    try {
      await userManagementAPI.updateRole(userId, newRole);
      fetchUsers();
      fetchStats();
      alert(t('userManagement.roleUpdated'));
    } catch (err) {
      alert(err.response?.data?.error || t('userManagement.failedToUpdateRole'));
    }
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    const action = currentStatus ? t('userManagement.deactivate') : t('userManagement.activate');
    if (!window.confirm(t('userManagement.statusChangeConfirm', { action }))) {
      return;
    }

    try {
      await userManagementAPI.toggleStatus(userId);
      fetchUsers();
      fetchStats();
      alert(t('userManagement.statusChanged', { action }));
    } catch (err) {
      alert(err.response?.data?.error || t('userManagement.failedToChangeStatus'));
    }
  };

  const handleApprove = async (userId) => {
    if (!window.confirm(t('userManagement.approveConfirm'))) {
      return;
    }

    try {
      await userManagementAPI.approveUser(userId);
      fetchUsers();
      fetchStats();
      alert(t('userManagement.userApproved'));
    } catch (err) {
      alert(err.response?.data?.error || t('userManagement.failedToApprove'));
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm(t('users.deleteConfirm'))) {
      return;
    }

    try {
      await userManagementAPI.delete(userId);
      fetchUsers();
      fetchStats();
      alert(t('userManagement.userDeleted'));
    } catch (err) {
      alert(err.response?.data?.error || t('userManagement.failedToDelete'));
    }
  };

  const getRoleBadgeClass = (role) => {
    const classes = {
      super_admin: 'bg-red-600 dark:bg-red-700 text-white',
      admin: 'bg-yellow-500 dark:bg-yellow-600 text-gray-900 dark:text-gray-100',
      accountant: 'bg-pastel-blue-600 dark:bg-pastel-blue-700 text-white',
      editor: 'bg-pastel-purple-600 dark:bg-pastel-purple-700 text-white',
      viewer: 'bg-gray-600 dark:bg-gray-700 text-white'
    };
    return classes[role] || 'bg-gray-600 dark:bg-gray-700 text-white';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (currentUser?.role !== 'super_admin') {
    return (
      <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200 px-6 py-4 rounded-xl">
{t('userManagement.accessDenied')}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-8 bg-gradient-to-r from-pastel-purple-600 to-pastel-blue-600 bg-clip-text text-transparent">
{t('users.title')}
      </h1>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200 px-6 py-4 rounded-xl mb-6">
          {error}
        </div>
      )}

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md shadow-xl rounded-2xl p-6 border border-pastel-purple-200 dark:border-pastel-purple-800 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">{t('userManagement.totalUsers')}</p>
            <p className="text-3xl font-bold text-pastel-purple-600 dark:text-pastel-purple-400">{stats.totalUsers}</p>
          </div>
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md shadow-xl rounded-2xl p-6 border border-pastel-green-200 dark:border-pastel-green-800 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">{t('userManagement.activeUsers')}</p>
            <p className="text-3xl font-bold text-pastel-green-600 dark:text-pastel-green-400">{stats.activeUsers}</p>
          </div>
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md shadow-xl rounded-2xl p-6 border border-gray-300 dark:border-gray-700 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">{t('userManagement.inactiveUsers')}</p>
            <p className="text-3xl font-bold text-gray-600 dark:text-gray-400">{stats.inactiveUsers}</p>
          </div>
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md shadow-xl rounded-2xl p-6 border border-yellow-200 dark:border-yellow-800 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">{t('userManagement.pendingApproval')}</p>
            <p className="text-3xl font-bold text-yellow-600 dark:text-yellow-400">{stats.pendingApproval || 0}</p>
          </div>
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md shadow-xl rounded-2xl p-6 border border-red-200 dark:border-red-800 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">{t('userManagement.superAdmins')}</p>
            <p className="text-3xl font-bold text-red-600 dark:text-red-400">{stats.usersByRole?.super_admin || 0}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('common.search')}</label>
          <input
            type="text"
            className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
            placeholder={t('userManagement.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('userManagement.roleFilter')}</label>
          <select
            className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
          >
            <option value="">{t('userManagement.allRoles')}</option>
            <option value="super_admin">{t('users.superAdmin')}</option>
            <option value="admin">{t('users.admin')}</option>
            <option value="accountant">{t('userManagement.accountant')}</option>
            <option value="editor">{t('userManagement.editor')}</option>
            <option value="viewer">{t('userManagement.viewer')}</option>
          </select>
        </div>
      </div>

      {/* Users Table */}
      {loading ? (
        <div className="min-h-screen flex items-center justify-center">
          <div className="flex flex-col items-center space-y-4">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-pastel-purple-500"></div>
            <p className="text-lg text-gray-700 dark:text-gray-300">{t('userManagement.loadingUsers')}</p>
          </div>
        </div>
      ) : (
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md shadow-xl rounded-2xl border border-pastel-purple-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gradient-to-r from-pastel-purple-500 to-pastel-blue-500">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">{t('users.username')}</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">{t('users.email')}</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">{t('userManagement.company')}</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">{t('users.role')}</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">{t('users.status')}</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">{t('userManagement.approval')}</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">{t('userManagement.authProvider')}</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">{t('userManagement.created')}</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {users.length > 0 ? (
                  users.map((user) => (
                    <tr
                      key={user.id}
                      className={`hover:bg-pastel-purple-50 dark:hover:bg-gray-900/30 transition-colors ${
                        !user.isActive ? 'bg-gray-50 dark:bg-gray-900/50' : ''
                      }`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                        {user.username}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                        {user.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                        {user.companySettings?.companyName || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getRoleBadgeClass(user.role)}`}>
                          {user.role.replace('_', ' ').toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                          user.isActive
                            ? 'bg-pastel-green-600 dark:bg-pastel-green-700 text-white'
                            : 'bg-gray-600 dark:bg-gray-700 text-white'
                        }`}>
                          {user.isActive ? t('users.active') : t('users.inactive')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                          user.isApproved
                            ? 'bg-pastel-green-600 dark:bg-pastel-green-700 text-white'
                            : 'bg-yellow-500 dark:bg-yellow-600 text-gray-900'
                        }`}>
                          {user.isApproved ? t('userManagement.approved') : t('userManagement.pending')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300 capitalize">
                        {user.authProvider}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                        {formatDate(user.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {user.id !== currentUser.id ? (
                          <div className="flex gap-2 flex-wrap">
                            {!user.isApproved && (
                              <button
                                onClick={() => handleApprove(user.id)}
                                className="px-3 py-1 text-xs font-medium bg-pastel-purple-600 hover:bg-pastel-purple-700 text-white rounded-lg shadow transition-all duration-300"
                              >
{t('userManagement.approve')}
                              </button>
                            )}
                            <select
                              className="px-2 py-1 text-xs bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
                              value={user.role}
                              onChange={(e) => handleRoleChange(user.id, e.target.value)}
                            >
                              <option value="super_admin">{t('users.superAdmin')}</option>
                              <option value="admin">{t('users.admin')}</option>
                              <option value="accountant">{t('userManagement.accountant')}</option>
                              <option value="editor">{t('userManagement.editor')}</option>
                              <option value="viewer">{t('userManagement.viewer')}</option>
                            </select>
                            <button
                              onClick={() => handleToggleStatus(user.id, user.isActive)}
                              className={`px-3 py-1 text-xs font-medium rounded-lg shadow transition-all duration-300 ${
                                user.isActive
                                  ? 'bg-yellow-500 hover:bg-yellow-600 text-gray-900'
                                  : 'bg-pastel-green-600 hover:bg-pastel-green-700 text-white'
                              }`}
                            >
{user.isActive ? t('userManagement.deactivate') : t('userManagement.activate')}
                            </button>
                            <button
                              onClick={() => handleDelete(user.id)}
                              className="px-3 py-1 text-xs font-medium bg-red-600 hover:bg-red-700 text-white rounded-lg shadow transition-all duration-300"
                            >
{t('common.delete')}
                            </button>
                          </div>
                        ) : (
                          <span className="text-gray-600 dark:text-gray-400 text-xs">{t('userManagement.currentUser')}</span>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="px-6 py-8 text-center text-gray-600 dark:text-gray-400">
                      {t('userManagement.noUsersFound')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
